import { EmailAnalysisParticipant, EmailAnalysisParticipantType } from "@types";

export const getCurrentContextEmailProps = (): Promise<{
  conversationId: string;
  draftHtml: string | null;
  draftText: string | null;
  messageId: string;
  outlookProfileEmail: string;
  subject: string;
  participants: EmailAnalysisParticipant[];
  recipients: EmailAnalysisParticipant[];
}> => {
  const isComposeMode = !!Office.context.mailbox?.item?.getItemIdAsync;

  if (isComposeMode) {
    return new Promise((resolve) => {
      Promise.all([
        new Promise<string>((resolve) => {
          Office.context.mailbox?.item?.getItemIdAsync
            ? Office.context.mailbox.item.getItemIdAsync((result) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                  const messageId = (result.value || "").replace(/\+/g, "_").replace(/\//g, "-");
                  resolve(messageId);
                } else {
                  resolve("");
                }
              })
            : resolve("");
        }),
        new Promise<string>((resolve) => {
          Office.context.mailbox?.item?.subject?.getAsync
            ? Office.context.mailbox.item.subject.getAsync((result) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                  resolve(result.value);
                } else {
                  resolve("");
                }
              })
            : resolve("");
        }),
        new Promise<EmailAnalysisParticipant | null>((resolve) => {
          Office.context.mailbox?.item?.from?.getAsync
            ? Office.context.mailbox.item.from.getAsync((result) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                  const { emailAddress, displayName } = result.value;
                  resolve({
                    email: emailAddress,
                    type: EmailAnalysisParticipantType.Sender,
                    metadata: {
                      name: displayName,
                      avatarSrc: null,
                    },
                  });
                } else {
                  resolve(null);
                }
              })
            : resolve(null);
        }),
        new Promise<EmailAnalysisParticipant[]>((resolve) => {
          Office.context.mailbox?.item?.to?.getAsync
            ? Office.context.mailbox.item.to.getAsync((result) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                  const toParticipants: EmailAnalysisParticipant[] = result.value.map(
                    ({ emailAddress, displayName }) => ({
                      email: emailAddress,
                      type: EmailAnalysisParticipantType.To,
                      metadata: {
                        name: displayName,
                        avatarSrc: null,
                      },
                    })
                  );
                  resolve(toParticipants);
                } else {
                  resolve([]);
                }
              })
            : resolve([]);
        }),
        new Promise<EmailAnalysisParticipant[]>((resolve) => {
          Office.context.mailbox?.item?.cc?.getAsync
            ? Office.context.mailbox.item.cc.getAsync((result) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                  const ccParticipants: EmailAnalysisParticipant[] = result.value.map(
                    ({ emailAddress, displayName }) => ({
                      email: emailAddress,
                      type: EmailAnalysisParticipantType.Cc,
                      metadata: {
                        name: displayName,
                        avatarSrc: null,
                      },
                    })
                  );
                  resolve(ccParticipants);
                } else {
                  resolve([]);
                }
              })
            : resolve([]);
        }),
        new Promise<EmailAnalysisParticipant[]>((resolve) => {
          Office.context.mailbox?.item?.bcc?.getAsync
            ? Office.context.mailbox.item.bcc.getAsync((result) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                  const bccParticipants: EmailAnalysisParticipant[] = result.value.map(
                    ({ emailAddress, displayName }) => ({
                      email: emailAddress,
                      type: EmailAnalysisParticipantType.Bcc,
                      metadata: {
                        name: displayName,
                        avatarSrc: null,
                      },
                    })
                  );
                  resolve(bccParticipants);
                } else {
                  resolve([]);
                }
              })
            : resolve([]);
        }),
        new Promise<string | null>((resolve) => {
          Office.context.mailbox?.item?.body?.getAsync
            ? Office.context.mailbox.item.body.getAsync(Office.CoercionType.Html, (result) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                  resolve(result.value);
                } else {
                  resolve(null);
                }
              })
            : resolve(null);
        }),
        new Promise<string | null>((resolve) => {
          Office.context.mailbox?.item?.body?.getAsync
            ? Office.context.mailbox.item.body.getAsync(Office.CoercionType.Text, (result) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                  resolve(result.value);
                } else {
                  resolve(null);
                }
              })
            : resolve(null);
        }),
      ]).then(
        ([
          messageId,
          subject,
          senderParticipant,
          toParticipants,
          ccParticipants,
          bccParticipants,
          draftHtml,
          draftText,
        ]) => {
          const outlookProfileEmail =
            Office.context.mailbox?.userProfile?.emailAddress?.toLowerCase() || "";
          const conversationId = (Office.context.mailbox?.item?.conversationId || "")
            .replace(/\+/g, "_")
            .replace(/\//g, "-");

          resolve({
            conversationId,
            messageId,
            outlookProfileEmail,
            subject,
            draftHtml,
            draftText,
            participants: [
              ...(senderParticipant ? [senderParticipant] : []),
              ...toParticipants,
              ...ccParticipants,
              ...bccParticipants,
            ],
            recipients: [...toParticipants, ...ccParticipants, ...bccParticipants],
          });
        }
      );
    });
  }

  return new Promise((resolve) => {
    const outlookProfileEmail =
      Office.context.mailbox?.userProfile?.emailAddress?.toLowerCase() || "";
    const conversationId = (Office.context.mailbox?.item?.conversationId || "")
      .replace(/\+/g, "_")
      .replace(/\//g, "-");
    const messageId = (Office.context.mailbox?.item?.itemId || "")
      .replace(/\+/g, "_")
      .replace(/\//g, "-");
    const subject = (Office.context.mailbox?.item?.subject as string) || "";

    const senderParticipant: EmailAnalysisParticipant | null = Office.context.mailbox?.item?.from
      ? {
          email: Office.context.mailbox.item.from.emailAddress,
          type: EmailAnalysisParticipantType.Sender,
          metadata: {
            name: Office.context.mailbox.item.from.displayName,
            avatarSrc: null,
          },
        }
      : null;

    const toParticipants: EmailAnalysisParticipant[] = (
      Office.context.mailbox?.item?.to || ([] as Office.EmailAddressDetails[])
    ).map((item) => ({
      email: item.emailAddress,
      type: EmailAnalysisParticipantType.To,
      metadata: {
        name: item.displayName,
        avatarSrc: null,
      },
    }));

    const ccParticipants: EmailAnalysisParticipant[] = (
      Office.context.mailbox?.item?.cc || ([] as Office.EmailAddressDetails[])
    ).map((item) => ({
      email: item.emailAddress,
      type: EmailAnalysisParticipantType.Cc,
      metadata: {
        name: item.displayName,
        avatarSrc: null,
      },
    }));

    const bccParticipants: EmailAnalysisParticipant[] = // @ts-ignore
      ((Office.context.mailbox?.item?.bcc as Office.EmailAddressDetails[]) || []).map((item) => ({
        email: item.emailAddress,
        type: EmailAnalysisParticipantType.Bcc,
        metadata: {
          name: item.displayName,
          avatarSrc: null,
        },
      }));

    resolve({
      conversationId,
      messageId,
      outlookProfileEmail,
      subject,
      draftHtml: null,
      draftText: null,
      participants: [
        ...(senderParticipant ? [senderParticipant] : []),
        ...toParticipants,
        ...ccParticipants,
        ...bccParticipants,
      ],
      recipients: [...toParticipants, ...ccParticipants, ...bccParticipants],
    });
  });
};
